import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Pencil, FileText } from 'lucide-react'; // Added FileText
import { supabase } from '../../../lib/supabase'; // Import supabase client
import EnhancedRentalContractsTable from './EnhancedRentalContractsTable';
import { supabaseRentalContractService } from '../../../services/supabase/rental-contracts';
import type { RentalContract, RentalContractFilter, RentalContractSortConfig, RentalContractFormData } from '../../../types/rental-contract';
import { TableColumn, ColumnWidth, SortConfig } from '../../../components/common/EnhancedTable/types';
import { usePermissions } from '../../../hooks/usePermissions';
import AddRentalContractModal from './components/AddRentalContractModal';
import EditRentalContractModal from './components/EditRentalContractModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import ViewUnderLeaseLotsModal from '../UnderLeases/components/ViewUnderLeaseLotsModal'; // Reusing this modal
import './EnhancedRentalContracts.css';

// Main component
export default function EnhancedRentalContracts() {
  const queryClient = useQueryClient();
  const { canEditLeases, isAdmin, canManageSystemSettings } = usePermissions();
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRentalContract, setSelectedRentalContract] = useState<RentalContract | null>(null);
  const [isViewLotsModalOpen, setIsViewLotsModalOpen] = useState(false);
  const [selectedSubleaseIdForLots, setSelectedSubleaseIdForLots] = useState<string | null>(null);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<RentalContractFilter>({
    leaseId: null,
    minAmount: null,
    maxAmount: null
  });
  const [sortConfig, setSortConfig] = useState<RentalContractSortConfig>({
    field: 'rental_trans_id',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Fetch rental contracts
  const [allLeaseLots, setAllLeaseLots] = useState<{ leaselot_lease: number; leaselot_lot: string }[]>([]);

  // Fetch rental contracts and all lease_lots
  const { data: rentalContracts = [], isLoading, error } = useQuery({
    queryKey: ['rental-contracts'],
    queryFn: async () => {
      const contracts = await supabaseRentalContractService.getAll();
      // Fetch all lease_lots records
      const { data: leaseLotsData, error: leaseLotsError } = await supabase
        .from('lease_lots')
        .select('leaselot_lease, leaselot_lot');
      
      if (leaseLotsError) {
        console.error('Error fetching allLeaseLots for RentalContracts:', leaseLotsError);
        setAllLeaseLots([]); // Set to empty if error
      } else if (leaseLotsData) {
        setAllLeaseLots(leaseLotsData as { leaselot_lease: number; leaselot_lot: string }[]);
      }
      return contracts;
    }
  });

  // Create a map of sublease_id to an array of lot_ids
  const leaseLotsMap = useMemo(() => {
    const map = new Map<number, string[]>();
    allLeaseLots.forEach(ll => {
      const lots = map.get(ll.leaselot_lease) || [];
      if (!lots.includes(ll.leaselot_lot)) { // Ensure unique lot_ids per lease
        lots.push(ll.leaselot_lot);
      }
      map.set(ll.leaselot_lease, lots);
    });
    return map;
  }, [allLeaseLots]);
  
  // Apply filters
  const filteredRentalContracts = useMemo(() => {
    return rentalContracts.filter(contract => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          contract.rental_trans_id.toString().includes(searchLower) ||
          (contract.rental_lease?.toString() || '').includes(searchLower) ||
          (contract.rental_amount?.toString() || '').includes(searchLower) ||
          (contract.sublease_info?.lot_number || '').toLowerCase().includes(searchLower) ||
          (contract.sublease_info?.community_name || '').toLowerCase().includes(searchLower) ||
          (contract.sublease_info?.holder_name || '').toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }
      
      // Lease ID filter
      if (filters.leaseId !== null && contract.rental_lease !== filters.leaseId) {
        return false;
      }
      
      // Min amount filter
      if (filters.minAmount !== null && (contract.rental_amount === null || contract.rental_amount < filters.minAmount)) {
        return false;
      }
      
      // Max amount filter
      if (filters.maxAmount !== null && (contract.rental_amount === null || contract.rental_amount > filters.maxAmount)) {
        return false;
      }
      
      return true;
    });
  }, [rentalContracts, searchQuery, filters]);
  
  // Apply sorting
  const sortedRentalContracts = useMemo(() => {
    return [...filteredRentalContracts].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'rental_trans_id':
          return direction * (a.rental_trans_id - b.rental_trans_id);
        case 'rental_lease':
          if (a.rental_lease === null) return direction;
          if (b.rental_lease === null) return -direction;
          return direction * (a.rental_lease - b.rental_lease);
        case 'rental_amount':
          if (a.rental_amount === null) return direction;
          if (b.rental_amount === null) return -direction;
          return direction * (a.rental_amount - b.rental_amount);
        case 'rental_start':
          if (a.rental_start === null) return direction;
          if (b.rental_start === null) return -direction;
          return direction * (new Date(a.rental_start).getTime() - new Date(b.rental_start).getTime());
        default:
          return 0;
      }
    });
  }, [filteredRentalContracts, sortConfig]);
  
  // Apply pagination
  const paginatedRentalContracts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedRentalContracts.slice(startIndex, endIndex);
  }, [sortedRentalContracts, currentPage, pageSize]);
  
  // Handle sorting
  const handleSort = useCallback((newSortConfig: SortConfig) => {
    // Convert SortConfig to RentalContractSortConfig
    setSortConfig({
      field: newSortConfig.field as RentalContractSortConfig['field'],
      direction: newSortConfig.direction
    });
  }, []);
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<RentalContractFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  }, []);
  
  // Handle remove filter
  const handleRemoveFilter = useCallback((key: keyof RentalContractFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  }, []);
  
  // Handle add rental contract
  const handleAddRentalContract = useCallback(async (formData: RentalContractFormData) => {
    try {
      await supabaseRentalContractService.create(formData);
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error adding rental contract:', error);
      // Handle error (e.g., show error message)
    }
  }, [queryClient]);
  
  // Handle edit rental contract
  const handleEditRentalContract = useCallback(async (formData: RentalContractFormData) => {
    if (!selectedRentalContract) return;
    
    try {
      await supabaseRentalContractService.update(selectedRentalContract.rental_trans_id, formData);
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
      setIsEditModalOpen(false);
      setSelectedRentalContract(null);
    } catch (error) {
      console.error('Error updating rental contract:', error);
      // Handle error (e.g., show error message)
    }
  }, [queryClient, selectedRentalContract]);
  
  // Handle delete rental contract
  const handleDeleteRentalContract = useCallback(async () => {
    if (!selectedRentalContract) return;
    
    try {
      await supabaseRentalContractService.delete(selectedRentalContract.rental_trans_id);
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
      setIsDeleteModalOpen(false);
      setSelectedRentalContract(null);
    } catch (error) {
      console.error('Error deleting rental contract:', error);
      // Handle error (e.g., show error message)
    }
  }, [queryClient, selectedRentalContract]);
  
  // Handle edit button click
  const handleEditClick = useCallback((rentalContract: RentalContract) => {
    setSelectedRentalContract(rentalContract);
    setIsEditModalOpen(true);
  }, []);

  const handleViewAssociatedLots = useCallback((contract: RentalContract) => {
    if (contract.rental_lease) {
      setSelectedSubleaseIdForLots(contract.rental_lease.toString());
      // For the modal title, we can use the rental contract's own ID as an identifier
      // const identifier = contract.rental_trans_id.toString(); 
      setIsViewLotsModalOpen(true);
    } else {
      console.warn('Parent sublease ID (rental_lease) is missing for this rental contract.');
      // Optionally, show an error to the user or disable the button
    }
  }, []);
  
  // Handle delete button click
  const handleDeleteClick = useCallback((rentalContract: RentalContract) => {
    setSelectedRentalContract(rentalContract);
    setIsDeleteModalOpen(true);
  }, []);
  
  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);
  
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);
  
  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedRentalContracts.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };
  
  // Extract unique lease IDs for filter dropdown
  const leaseIds = useMemo(() => {
    const ids = new Set<number>();
    rentalContracts.forEach(contract => {
      if (contract.rental_lease !== null) {
        ids.add(contract.rental_lease);
      }
    });
    return Array.from(ids).sort((a, b) => a - b);
  }, [rentalContracts]);

  // Format currency
  const formatCurrency = (value: number | null) => {
    if (value === null) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Define table columns
  const columns: TableColumn<RentalContract>[] = [
    {
      id: 'actions',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          {(isAdmin || canManageSystemSettings) && (
            <button
              onClick={() => handleEditClick(row)}
              className="action-button edit"
              title="Edit Rental Contract"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
      width: 'narrow' as ColumnWidth, // Changed to narrow
      align: 'center',
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'lots_list_icon',
      header: 'Lots List',
      accessor: (row) => (
        <button
          onClick={() => handleViewAssociatedLots(row)}
          className="w-8 h-8 flex items-center justify-center text-primary hover:text-primary-dark bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          title="View Associated Lots"
          disabled={!row.rental_lease} // Disable if no parent lease ID
        >
          <FileText className="h-4 w-4" />
        </button>
      ),
      width: 'narrow' as ColumnWidth, // Changed to narrow
      align: 'center',
    },
    {
      id: 'associated_lots_text',
      header: 'Associated Lots',
      accessor: (row) => {
        const associatedLotIds = row.rental_lease ? leaseLotsMap.get(row.rental_lease) || [] : [];
        const lotsString = associatedLotIds.join(', ');
        return (
          <div className="truncate" title={lotsString || 'N/A'}>
            {lotsString || 'N/A'}
          </div>
        );
      },
      width: 'narrow' as ColumnWidth, // Reduced width to allow Lease column to be fully visible
    },
    {
      id: 'leaseholder',
      header: 'LEASEHOLDER',
      accessor: (row) => {
        const holderName = row.sublease_info?.holder_name || 'N/A';
        return (
          <div className="truncate" title={holderName}>
            {holderName}
          </div>
        );
      },
      width: 'wide' as ColumnWidth,
      align: 'left',
      sortable: false
    },
    {
      id: 'rental_amount',
      header: 'Amount',
      accessor: (row) => {
        const formattedCurrency = formatCurrency(row.rental_amount);
        return (
          <div className="truncate currency-cell" title={formattedCurrency}>
            {formattedCurrency}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      align: 'right',
      sortable: true
    },
    {
      id: 'rental_start',
      header: 'Start Date',
      accessor: (row) => {
        const formattedDate = row.rental_start 
          ? new Date(row.rental_start).toLocaleDateString('en-AU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
          : 'N/A';
        return (
          <div className="truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    /* Commented out as per request - Trans ID column hidden from table
    {
      id: 'rental_trans_id',
      header: 'Trans ID',
      accessor: (row) => (
        <div className="truncate" title={row.rental_trans_id.toString()}>
          {row.rental_trans_id}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    */
    {
      id: 'rental_lease',
      header: 'Lease',
      accessor: (row) => {
        if (!row.rental_lease) return 'N/A';
        
        if (row.sublease_info) {
          const { lot_number, community_name, holder_name } = row.sublease_info;
          
          let displayText = `Lease #${row.rental_lease}`;
          
          if (lot_number && community_name) {
            displayText += ` - Lot ${lot_number} (${community_name})`;
          }
          
          if (holder_name) {
            displayText += ` - ${holder_name}`;
          }
          
          return (
            <div className="truncate" title={displayText}>
              {displayText}
            </div>
          );
        }
        
        return (
          <div className="truncate" title={`Lease #${row.rental_lease}`}>
            Lease #{row.rental_lease}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading rental contract data. Please try again later.
      </div>
    );
  }
  
  return (
    <div className="enhanced-rental-contracts-container p-4">
      <div className="enhanced-rental-contracts-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Rental Contracts</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Rental Contract
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search rental contracts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Rental Contracts: {sortedRentalContracts.length}
            </div>
          </div>
          
          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.leaseId !== null && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Lease ID:</span>
                  <span>{filters.leaseId}</span>
                  <button
                    onClick={() => handleRemoveFilter('leaseId')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.minAmount !== null && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Min Amount:</span>
                  <span>${filters.minAmount.toFixed(2)}</span>
                  <button
                    onClick={() => handleRemoveFilter('minAmount')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.maxAmount !== null && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Max Amount:</span>
                  <span>${filters.maxAmount.toFixed(2)}</span>
                  <button
                    onClick={() => handleRemoveFilter('maxAmount')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    leaseId: null,
                    minAmount: null,
                    maxAmount: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Lease ID filter */}
                <div className="filter-group">
                  <label htmlFor="lease-filter">Lease ID</label>
                  <select
                    id="lease-filter"
                    value={filters.leaseId || ''}
                    onChange={(e) => handleFilterChange({ leaseId: e.target.value ? parseInt(e.target.value, 10) : null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All leases</option>
                    {leaseIds.map(id => (
                      <option key={id} value={id}>{id}</option>
                    ))}
                  </select>
                </div>
                
                {/* Min amount filter */}
                <div className="filter-group">
                  <label htmlFor="min-amount-filter">Min Amount</label>
                  <input
                    id="min-amount-filter"
                    type="number"
                    value={filters.minAmount || ''}
                    onChange={(e) => handleFilterChange({ minAmount: e.target.value ? parseFloat(e.target.value) : null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                {/* Max amount filter */}
                <div className="filter-group">
                  <label htmlFor="max-amount-filter">Max Amount</label>
                  <input
                    id="max-amount-filter"
                    type="number"
                    value={filters.maxAmount || ''}
                    onChange={(e) => handleFilterChange({ maxAmount: e.target.value ? parseFloat(e.target.value) : null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="enhanced-rental-contracts-table-container enhanced-table-force-scroll">
        <EnhancedRentalContractsTable
          columns={columns}
          data={paginatedRentalContracts}
          loading={isLoading}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          keyField="rental_trans_id"
          width="1500px"
          horizontalScroll={true}
          maxHeight="calc(100vh - 20rem)"
          emptyMessage="No rental contracts found"
          alternateRowColors={true}
          permissions={{
            isAdmin,
            canManageSystemSettings,
            canEditLeases
          }}
        />
      </div>
      
      {/* Add Modal */}
      {isAddModalOpen && (
        <AddRentalContractModal
          isOpen={true}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
          }}
        />
      )}
      
      {/* Edit Modal */}
      {selectedRentalContract && isEditModalOpen && (
        <EditRentalContractModal
          isOpen={true}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRentalContract(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
          }}
          rentalContract={selectedRentalContract}
        />
      )}
      
      {/* View Lots Modal */}
      {selectedSubleaseIdForLots && (
        <ViewUnderLeaseLotsModal
          isOpen={isViewLotsModalOpen}
          onClose={() => {
            setIsViewLotsModalOpen(false);
            setSelectedSubleaseIdForLots(null);
          }}
          subleaseId={selectedSubleaseIdForLots}
          underLeaseIdentifier={`Rental Contract (Parent SubLease ID: ${selectedSubleaseIdForLots})`}
        />
      )}

      {/* Delete Confirmation Modal */}
      {selectedRentalContract && isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={true}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedRentalContract(null);
          }}
          onConfirm={handleDeleteRentalContract}
          title="Delete Rental Contract"
          message={`Are you sure you want to delete rental contract #${selectedRentalContract.rental_trans_id}? This action cannot be undone.`}
        />
      )}
    </div>
  );
}
